/* ============================================================
   SUNSET BOUTIQUE — theme.js
   ============================================================ */

'use strict';

/* --- Cart API --------------------------------------------- */
const CartAPI = {
  async add(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    if (!res.ok) throw new Error('Failed to add to cart');
    return res.json();
  },

  async get() {
    const res = await fetch('/cart.js');
    return res.json();
  },

  async updateItem(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    return res.json();
  },

  updateCount(count) {
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = count;
    });
  }
};

/* --- Mobile Menu ------------------------------------------ */
(function initMobileMenu() {
  const toggle = document.querySelector('.mobile-toggle');
  const menu = document.getElementById('mobile-menu');
  const closeBtn = document.querySelector('.mobile-menu-close');

  if (!toggle || !menu) return;

  toggle.addEventListener('click', () => {
    menu.classList.add('open');
    toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      menu.classList.remove('open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    });
  }

  // Close on overlay click
  menu.addEventListener('click', e => {
    if (e.target === menu) {
      menu.classList.remove('open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  });

  // Close on Escape
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && menu.classList.contains('open')) {
      menu.classList.remove('open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  });
})();

/* --- Size Guide Modal ------------------------------------- */
(function initSizeModal() {
  document.querySelectorAll('.size-guide-link').forEach(btn => {
    btn.addEventListener('click', e => {
      e.preventDefault();
      const modal = document.getElementById('size-modal');
      if (modal) modal.classList.add('open');
    });
  });

  const closeBtn = document.getElementById('size-modal-close');
  const modal = document.getElementById('size-modal');

  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      modal?.classList.remove('open');
    });
  }

  if (modal) {
    modal.addEventListener('click', e => {
      if (e.target.id === 'size-modal') {
        modal.classList.remove('open');
      }
    });
  }

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') modal?.classList.remove('open');
  });
})();

/* --- Variant Selection ------------------------------------ */
function updateVariant() {
  if (!window.productData) return;

  // Selectors render in option order, so match each choice to its own option position.
  // (Sets have two size options with the same values, so "contains" matching picks wrong variants.)
  const selectors = document.querySelectorAll('.selector');
  const opts = [];
  selectors.forEach(sel => {
    const active = sel.querySelector('.swatch.active, .size-btn.active');
    opts.push(active ? active.dataset.value : null);
  });

  if (opts.some(o => o === null)) return;

  const variant = window.productData.variants.find(v =>
    opts.every((o, i) => v.options[i] === o)
  );

  if (variant) {
    const vidInput = document.getElementById('variant-id');
    if (vidInput) vidInput.value = variant.id;

    const addBtn = document.getElementById('add-to-cart');
    const price = '$' + (variant.price / 100).toFixed(2);

    if (addBtn) {
      addBtn.disabled = !variant.available;
      if (variant.available) {
        addBtn.textContent = 'Add to Bag — ' + price;
        addBtn.style.background = '';
      } else {
        addBtn.textContent = 'Sold Out';
        addBtn.style.background = 'var(--ink-soft)';
      }
    }

    // Update main image if variant has an image
    if (variant.featured_image) {
      const mainImg = document.getElementById('main-product-image');
      if (mainImg) {
        mainImg.srcset = '';
        mainImg.src = variant.featured_image.src;
        mainImg.alt = variant.featured_image.alt || '';
      }
    }
  }
}

/* --- Swatch Clicks ---------------------------------------- */
(function initSwatches() {
  document.querySelectorAll('.swatch').forEach(swatch => {
    swatch.addEventListener('click', () => {
      const selector = swatch.closest('.selector');
      if (!selector) return;

      // Remove active from siblings
      selector.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
      swatch.classList.add('active');

      // Update label
      const label = selector.querySelector('.selector-label span');
      if (label) label.textContent = swatch.dataset.value;

      updateVariant();
    });
  });
})();

/* --- Size Button Clicks ----------------------------------- */
(function initSizeButtons() {
  document.querySelectorAll('.size-btn:not(.soldout)').forEach(btn => {
    btn.addEventListener('click', () => {
      const selector = btn.closest('.selector');
      if (!selector) return;

      // Remove active from siblings
      selector.querySelectorAll('.size-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      // Update label
      const label = selector.querySelector('.selector-label span');
      if (label) label.textContent = btn.dataset.value;

      updateVariant();
    });
  });
})();

/* --- Add to Cart Form ------------------------------------- */
(function initAddToCart() {
  const form = document.getElementById('product-form');
  if (!form) return;

  form.addEventListener('submit', async e => {
    e.preventDefault();

    const btn = document.getElementById('add-to-cart');
    const vid = document.getElementById('variant-id')?.value;

    // Check every size option has a selection
    const missing = [...document.querySelectorAll('.selector')].find(sel =>
      sel.querySelector('.size-btn') && !sel.querySelector('.size-btn.active')
    );
    if (missing) {
      const orig = btn.textContent;
      const name = (missing.dataset.option || 'size').toLowerCase();
      btn.textContent = name.includes('size') ? 'Please select ' + name : 'Please select a size';
      btn.style.background = 'var(--accent-deep)';
      setTimeout(() => {
        btn.textContent = orig;
        btn.style.background = '';
      }, 1600);
      return;
    }

    if (!vid) return;

    btn?.classList.add('loading');
    const orig = btn?.textContent;

    try {
      await CartAPI.add(vid);
      const cart = await CartAPI.get();
      CartAPI.updateCount(cart.item_count);

      if (btn) {
        btn.textContent = '✓ Added to Bag';
        btn.style.background = 'var(--accent-deep)';
        setTimeout(() => {
          btn.textContent = orig;
          btn.style.background = '';
        }, 1600);
      }
    } catch (err) {
      console.error('Add to cart error:', err);
      if (btn) {
        btn.textContent = 'Error — try again';
        btn.style.background = '#c44';
        setTimeout(() => {
          btn.textContent = orig;
          btn.style.background = '';
        }, 2000);
      }
    } finally {
      btn?.classList.remove('loading');
    }
  });
})();

/* --- Gallery Thumbnails ----------------------------------- */
(function initGallery() {
  const thumbs = document.querySelectorAll('.thumb');
  const mainImg = document.getElementById('main-product-image');

  if (!thumbs.length || !mainImg) return;

  const showImage = thumb => {
    // Update main image (srcset must change too, or the browser keeps the old photo)
    const src = thumb.dataset.imgSrc;
    if (src) {
      mainImg.srcset = thumb.dataset.imgSrcset || '';
      mainImg.src = src;
      if (thumb.dataset.imgAlt) mainImg.alt = thumb.dataset.imgAlt;
    }

    // Update active state
    thumbs.forEach(t => {
      t.classList.remove('active');
      t.setAttribute('aria-pressed', 'false');
    });
    thumb.classList.add('active');
    thumb.setAttribute('aria-pressed', 'true');
  };

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => showImage(thumb));
    thumb.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        showImage(thumb);
      }
    });
  });
})();

/* --- Collection Sort & Filter ----------------------------- */
(function initCollectionControls() {
  const sortSelect = document.getElementById('sort-by');
  if (sortSelect) {
    sortSelect.addEventListener('change', e => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      window.location = url.toString();
    });
  }

  document.querySelectorAll('.filter-pill').forEach(pill => {
    pill.addEventListener('click', () => {
      if (pill.dataset.url) window.location = pill.dataset.url;
    });
  });
})();

/* --- FAQ Navigation --------------------------------------- */
(function initFAQNav() {
  const navItems = document.querySelectorAll('.faq-nav li');
  if (!navItems.length) return;

  navItems.forEach(item => {
    item.addEventListener('click', () => {
      const target = item.dataset.target;
      if (!target) return;

      const section = document.getElementById(target);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      navItems.forEach(n => n.classList.remove('active'));
      item.classList.add('active');
    });
  });

  // Highlight active nav on scroll
  const sections = document.querySelectorAll('.faq-section[id]');
  if (sections.length) {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          navItems.forEach(item => {
            item.classList.toggle('active', item.dataset.target === id);
          });
        }
      });
    }, { rootMargin: '-30% 0px -60% 0px' });

    sections.forEach(s => observer.observe(s));
  }
})();

/* --- Cart Page Quantity Controls -------------------------- */
(function initCartPage() {
  document.querySelectorAll('.cart-qty-increase').forEach(btn => {
    btn.addEventListener('click', async () => {
      const input = btn.closest('.cart-qty')?.querySelector('input');
      if (!input) return;
      const newQty = parseInt(input.value) + 1;
      await updateCartItem(input.dataset.key, newQty, input);
    });
  });

  document.querySelectorAll('.cart-qty-decrease').forEach(btn => {
    btn.addEventListener('click', async () => {
      const input = btn.closest('.cart-qty')?.querySelector('input');
      if (!input) return;
      const newQty = Math.max(0, parseInt(input.value) - 1);
      await updateCartItem(input.dataset.key, newQty, input);
    });
  });

  document.querySelectorAll('.cart-remove').forEach(btn => {
    btn.addEventListener('click', async () => {
      const key = btn.dataset.key;
      if (key) await updateCartItem(key, 0, null);
    });
  });

  async function updateCartItem(key, qty, input) {
    try {
      const cart = await CartAPI.updateItem(key, qty);
      CartAPI.updateCount(cart.item_count);
      // Reload to reflect updated cart
      window.location.reload();
    } catch (err) {
      console.error('Cart update error:', err);
    }
  }
})();

/* --- Cart Count Init -------------------------------------- */
(function initCartCount() {
  CartAPI.get().then(cart => {
    CartAPI.updateCount(cart.item_count);
  }).catch(() => {
    // Silently fail if not on a Shopify store
  });
})();

/* --- Smooth scroll for anchor links ----------------------- */
document.querySelectorAll('a[href^="#"]').forEach(link => {
  link.addEventListener('click', e => {
    const target = document.querySelector(link.getAttribute('href'));
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
});
